import React, { lazy, Suspense, useState, useEffect, useMemo, useRef } from "react";

const DEFAULT_HERO_COPY = {
  line1: "Welcome to Eqlize Now!",
  line2: "The algorithm wasn't built for us. So we built communities instead.",
  line3: "People helping people.",
};
import Button from "../components/common/Button";
import { financeItems } from "../utils/constants";
import getAppRight from "../assets/landing/getAppRight.webp";
import playstore from "../assets/landing/googleplay.svg";
// import appStore from "../assets/landing/appstore.svg";
import appstore from "../assets/landing/appstore1.svg";
import { useNavigate } from "react-router-dom";
import { isOnboardingPageEnabled, ONBOARDING_PATH } from "../utils/paymentNav";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "react-router-dom";
import { useDispatch } from "react-redux";
import {
  HiOutlineShieldCheck,
  HiOutlineBanknotes,
  HiOutlineSparkles,
} from "react-icons/hi2";
import { formatNumber } from "../Helper/fotmateNumber";
import { useSelector } from "react-redux";
import { getLocalToken } from "../Helper/getToken";
import { setWeekelyStats } from "../redux/slices/authSlice";
import { getUser } from "../utils/helpers";
import { GlassBackground } from "../theme";

const HERO_ICON_CDN_BASE = "https://d14fo9r4owogdf.cloudfront.net/g7Images/media/hero-icons";

// Animated words + matching colorful icons for the hero section.
const ANIMATED_ITEMS = [
  { text: "your new home", icon: `${HERO_ICON_CDN_BASE}/home-selected-state-svgrepo-com.svg`, iconAlt: "Home" },
  { text: "your college tuition", icon: `${HERO_ICON_CDN_BASE}/graduation-cap-svgrepo-com.svg`, iconAlt: "Graduation cap" },
  { text: "your start-up", icon: `${HERO_ICON_CDN_BASE}/startup-rocket-svgrepo-com.svg`, iconAlt: "Start-up rocket" },
  { text: "your home remodel", icon: `${HERO_ICON_CDN_BASE}/home-selected-state-svgrepo-com.svg`, iconAlt: "Home" },
  { text: "your community park project", icon: `${HERO_ICON_CDN_BASE}/park-svgrepo-com.svg`, iconAlt: "Park" },
  { text: "your medical bills", icon: `${HERO_ICON_CDN_BASE}/medical-record-svgrepo-com.svg`, iconAlt: "Medical record" },
  { text: "your new car", icon: `${HERO_ICON_CDN_BASE}/car-svgrepo-com.svg`, iconAlt: "Car" },
  { text: "anything important", icon: `${HERO_ICON_CDN_BASE}/star-track-svgrepo-com.svg`, iconAlt: "Star" },
];

const FEATURED_FALLBACK_VARIANTS = ["drift", "pulse", "cross"];

const COMMUNITY_ICON_PALETTES = {
  steady: ["#2E5BFF", "#4F7DFF", "#67C6FF", "#21B6D7", "#0E2D87"],
  turbo: ["#FF7A00", "#FF934F", "#FFD166", "#FF4D2D", "#9A3412"],
  flash: ["#FF3F7A", "#FF5DB1", "#B66DFF", "#53D0FF", "#7B1FA2"],
  hypersonic: ["#7C3AED", "#A855F7", "#EC4899", "#38BDF8", "#1D4ED8"],
  wave: ["#00A6A6", "#2DD4BF", "#70F2D4", "#0EA5E9", "#0A5B78"],
  spark: ["#9656FF", "#B999FF", "#F472B6", "#60A5FA", "#4C2A88"],
};

const DEFAULT_FALLBACK_PALETTES = [
  // Fresh warm-citrus + mint family only. Format:
  // [gradient start, radial accent, gradient end].
  ["#F7A75A", "#FFD8A8", "#E7822E"], // peach sorbet
  ["#F6B26A", "#FFE3B8", "#E78F3D"], // apricot
  ["#F79A45", "#FFD39A", "#DE7425"], // tangerine
  ["#F6A971", "#DFF7EC", "#76C7AA"], // peach + mint blend
  ["#FFC06F", "#BFF3DF", "#5EBFA1"], // citrus mint
  ["#F7AF7A", "#CFF6E8", "#71CBB0"], // melon mint
];

const FEATURED_CONNECTOR_CURVES = [
  "M4,16 C20,4 48,4 72,16",
  "M4,14 C22,24 50,24 72,14",
  "M4,16 C22,8 50,24 72,16",
  "M4,16 C24,28 52,6 72,16",
  "M4,16 C24,10 52,10 72,16",
];

const hashString = (value = "") => {
  let hash = 2166136261;
  for (let i = 0; i < value.length; i += 1) {
    hash ^= value.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash >>> 0);
};

const createSeededRandom = (seed) => {
  let state = seed % 2147483647;
  if (state <= 0) state += 2147483646;
  return () => {
    state = (state * 16807) % 2147483647;
    return (state - 1) / 2147483646;
  };
};

const shuffleWithSeed = (items, seed) => {
  const random = createSeededRandom(seed || 1);
  const output = [...items];
  for (let i = output.length - 1; i > 0; i -= 1) {
    const randomIndex = Math.floor(random() * (i + 1));
    [output[i], output[randomIndex]] = [output[randomIndex], output[i]];
  }
  return output;
};

const createUniqueFeaturedPalette = (cardIndex, seed) => {
  // Ensure every card gets a distinct warm/mint palette instance instead of
  // cycling a finite list (which can repeat colors across cards).
  const warmHue = 24 + ((cardIndex * 19 + seed) % 16); // softer peach: 24..39
  const mintHue = 154 + ((cardIndex * 29 + seed) % 24); // mint/teal: 154..177

  // Mint-forward blend: keep peach as a light top tone, push accent + depth
  // into mint/teal so cards feel fresher and less orange-heavy.
  const c1 = `hsl(${warmHue} 84% 72%)`;
  const c2 = `hsl(${mintHue} 76% 86%)`;
  const c3 = `hsl(${mintHue} 62% 58%)`;

  return [c1, c2, c3];
};

const FAQ = lazy(() => import("../components/FAQ"));
const BrightFuture = lazy(() => import("../components/common/BrightFuture"));
const Modals = lazy(() => import("../components/common/Modals"));
const loadAuthActions = () => import("../redux/actions/auth");
const createStripePaymentIntentLazy = async (loginId, chainId, nodeId) =>
  (await loadAuthActions()).createStripePaymentIntent(loginId, chainId, nodeId);
const getChainsListLazy = async () => (await loadAuthActions()).getChainsList();
const getFeaturedCampaignsLazy = async (limit) =>
  (await loadAuthActions()).getFeaturedCampaigns(limit);
const getThresholdValueLazy = async () =>
  (await loadAuthActions()).getThreshHoldValue();
const getTopNodesLazy = async () => (await loadAuthActions()).getTopNodes();
const searchNodeLazy = async (value) => (await loadAuthActions()).searchNode(value);

const getVideoEmbedUrl = (videoUrl = "") => {
  if (!videoUrl) return "";

  try {
    const url = new URL(videoUrl);
    const host = url.hostname.replace(/^www\./, "").toLowerCase();

    if (host === "youtu.be") {
      const videoId = url.pathname.replace(/^\//, "");
      return videoId ? `https://www.youtube-nocookie.com/embed/${videoId}` : "";
    }

    if (host.includes("youtube.com")) {
      const videoId = url.searchParams.get("v");
      return videoId ? `https://www.youtube-nocookie.com/embed/${videoId}` : "";
    }

    if (host.includes("vimeo.com")) {
      const videoId = url.pathname.split("/").filter(Boolean).pop();
      return videoId ? `https://player.vimeo.com/video/${videoId}` : "";
    }

    if (host.includes("loom.com")) {
      const parts = url.pathname.split("/").filter(Boolean);
      const videoId = parts[parts.length - 1];
      return videoId ? `https://www.loom.com/embed/${videoId}` : "";
    }
  } catch (error) {
    return "";
  }

  return "";
};

const isDirectVideoUrl = (value = "") => {
  return /\.(mp4|webm|ogg|mov|m4v)(\?.*)?$/i.test(String(value || ""));
};

const resolveCampaignMedia = (item) => {
  const mediaType = String(item?.campaignMediaType || "").toLowerCase();
  const imageUrl =
    item?.campaignHeroImage ||
    item?.campaignImage ||
    item?.heroImage ||
    item?.image ||
    "";
  const videoUrl =
    item?.campaignVideoUrl ||
    item?.videoUrl ||
    item?.video ||
    "";

  const hasImage = Boolean(imageUrl);
  const hasVideo = Boolean(videoUrl);

  if (mediaType === "video" && hasVideo) {
    return { type: "video", imageUrl, videoUrl };
  }
  if (mediaType === "image" && hasImage) {
    return { type: "image", imageUrl, videoUrl };
  }
  if (hasImage) {
    return { type: "image", imageUrl, videoUrl };
  }
  if (hasVideo) {
    return { type: "video", imageUrl, videoUrl };
  }
  return { type: "none", imageUrl: "", videoUrl: "" };
};

const getFeaturedTime = (item) => {
  const candidates = [
    item?.featured?.startAt,
    item?.featured?.createdAt,
    item?.featuredAt,
    item?.updatedAt,
    item?.createdAt,
  ];
  for (const candidate of candidates) {
    const time = new Date(candidate || 0).getTime();
    if (!Number.isNaN(time) && time > 0) return time;
  }
  return 0;
};

const seedRandom = (seed) => {
  let state = seed % 2147483647;
  if (state <= 0) state += 2147483646;
  return () => {
    state = (state * 16807) % 2147483647;
    return (state - 1) / 2147483646;
  };
};

const shuffleBySeed = (list, seed) => {
  const random = seedRandom(seed || 1);
  const output = [...list];
  for (let i = output.length - 1; i > 0; i -= 1) {
    const j = Math.floor(random() * (i + 1));
    [output[i], output[j]] = [output[j], output[i]];
  }
  return output;
};

const HeroAnimatedLine = React.memo(function HeroAnimatedLine() {
  const [animatedIdx, setAnimatedIdx] = useState(0);
  const [fadeState, setFadeState] = useState("fade-in");
  const animatedTimeout = useRef();

  useEffect(() => {
    if (fadeState === "fade-in") {
      animatedTimeout.current = setTimeout(() => setFadeState("hold"), 700);
    } else if (fadeState === "hold") {
      animatedTimeout.current = setTimeout(() => setFadeState("fade-out"), 1700);
    } else {
      animatedTimeout.current = setTimeout(() => {
        setAnimatedIdx((prev) => (prev + 1) % ANIMATED_ITEMS.length);
        setFadeState("fade-in");
      }, 700);
    }

    return () => clearTimeout(animatedTimeout.current);
  }, [animatedIdx, fadeState]);

  const fadeClass =
    fadeState === "fade-in"
      ? "glass-hero-fade-in"
      : fadeState === "hold"
        ? "glass-hero-hold"
        : "glass-hero-fade-out";

  return (
    <div className="pt-10 flex flex-row justify-center items-baseline min-h-[2.5em]">
      <span className="glass-hero-kicker-text 4xll:text-40 4xl:text-38 3xll:text-36 3xl:text-34 2xll:text-32 2xl:text-30 xl:text-28 lg:text-26 md:text-22 xs:text-16 font-semibold text-gray-900">
        Fund
      </span>
      <span className="glass-hero-inline-gap">&nbsp;</span>
      <span
        className={`glass-hero-animated-word glass-hero-animated-text ${fadeClass} 4xll:text-40 4xl:text-38 3xll:text-36 3xl:text-34 2xll:text-32 2xl:text-30 xl:text-28 lg:text-26 md:text-22 xs:text-16 font-extrabold text-gray-600`}
      >
        {ANIMATED_ITEMS[animatedIdx]?.text}
      </span>
      <span
        className={`glass-hero-inline-icon glass-hero-animated-text ${fadeClass}`}
        aria-hidden="true"
      >
        <img
          src={ANIMATED_ITEMS[animatedIdx]?.icon}
          alt={ANIMATED_ITEMS[animatedIdx]?.iconAlt || "Animated icon"}
          className="h-8 w-8 sm:h-9 sm:w-9 md:h-10 md:w-10 object-contain"
          width="40"
          height="40"
          loading="lazy"
          decoding="async"
        />
      </span>
    </div>
  );
});

const InviteFeatureIcon = ({ type }) => {
  if (type === "secure") {
    return <HiOutlineShieldCheck className="glass-invite-icon" aria-hidden="true" />;
  }

  if (type === "withdraw") {
    return <HiOutlineBanknotes className="glass-invite-icon" aria-hidden="true" />;
  }

  return <HiOutlineSparkles className="glass-invite-icon" aria-hidden="true" />;
};

const LandingPage = ({ isModalOpend, closeModal }) => {
  const landingRootRef = useRef(null);

  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  // const [setitem, setSetItem] = useState("");
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const paymentId = queryParams.get("paymentId");

  let paymnetchainID = queryParams.get("chainId");
  let chainId = queryParams.get("chainId");
  const [aftersigninnodesId] = useState(queryParams.get("nodesId"));
  const [stripePaymentID, setstripePaymentID] = useState();
  const [paymnetnodeId, setPaymnetNodeId] = useState({});
  const [chainn, setChainn] = useState("");
  const [nodesId, setnodesId] = useState("");

  useEffect(() => {
    setstripePaymentID(queryParams.get("paymentId"));
  }, [location.search]);

  const [state, setState] = useState({
    tenNodes: [],
    inputData: "",
  });
  const featuredMixSeedRef = useRef(Math.floor(Date.now() / 1000));

  const navigateToUrl = (url) => {
    window.location.href = url;
  };

  const navigateToAnchor = (anchor) => {
    window.location.hash = anchor;
  };

  const token = getLocalToken();

  const [isopen, setIsOpen] = useState(false);
  function handleToggle(nodeId, chainId) {
    setIsOpen(!isopen);
    
    
    if (token) {
      navigate("/");
      
    } else {
      
      setPaymnetNodeId({ nodeId, chainId });
      
      if (paymentId) {
       
        navigate(`/sign-in?paymentId=${paymentId}&intent=join-campaign`);
      } else if (chainId) {
       
        navigate(`/sign-in?chainId=${chainId}&intent=join-campaign`);
      } else {
       
        console.log("");
      }
    }
  }

  const handleJoin = (id, chain, type, campaignName) => {
    if (getUser()) {
      // Onboarding-page flow: route signed-in users straight to
      // /onboarding with chain/node context instead of opening the
      // legacy payment modal.
      if (isOnboardingPageEnabled()) {
        const qs = new URLSearchParams();
        if (chain) qs.set("chainId", chain);
        if (id) qs.set("nodesId", id);
        if (campaignName) qs.set("campaignName", campaignName);
        const q = qs.toString();
        // R4.2 (perf): fire-and-forget the Stripe PaymentIntent under
        // the SAME react-query key /onboarding uses, so by the time
        // the navigation completes the intent is already in cache and
        // /onboarding can paint Stripe + PayPal in the first frame.
        const prefetchLoginID = userData?._id;
        if (prefetchLoginID && chain) {
          queryClient
            .prefetchQuery({
              queryKey: [
                "onboardingCheckoutBundle",
                prefetchLoginID,
                chain,
                id || "",
              ],
              queryFn: async () => {
                const envelope = await createStripePaymentIntent(
                  prefetchLoginID,
                  chain,
                  id || ""
                );
                return envelope?.data || null;
              },
              staleTime: 5 * 60 * 1000,
            })
            .catch(() => {
              // Best-effort — /onboarding will retry on mount if this
              // misses (no UX impact, just no speed-up).
            });
        }
        navigate(`${ONBOARDING_PATH}${q ? `?${q}` : ""}`);
        return;
      }
      setIsOpen(true);
      if (token) {
        if (type === "chain") {
          if (chain) {
            setChainn(chain);
            setnodesId(id);
          } else {
            chainId = chain;
            // setnodesId(id);
          }
        }
      }
    } else {
      setnodesId(id);
      navigate(`/sign-in?chainId=${chain}&nodesId=${id}&intent=join-campaign`);
    }
  };

  const openCampaignDetails = (id, chain) => {
    if (!id) return;
    const params = new URLSearchParams();
    params.set("id", String(id));
    if (chain) params.set("chain", String(chain));
    navigate(`/node-details?${params.toString()}`);
  };

  // Shared cache with /search and /onboarding (queryKey: "chains-list").
  // The home page no longer renders the streams list, but we keep this
  // query alive so the chains-list payload — which now also carries the
  // Stripe publishable key + PayPal clientId — is already in cache by
  // the time the user clicks Get Started and lands on /onboarding.
  const { data: chainsListData = [] } = useQuery({
    queryKey: ["chains-list"],
    queryFn: getChainsListLazy,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
  });

  // Cheapest stream — used for the Get Started PaymentIntent prefetch
  // and to mirror /onboarding's R4.1 default selection.
  const cheapestChain = useMemo(() => {
    if (!Array.isArray(chainsListData) || chainsListData.length === 0)
      return null;
    return chainsListData
      .slice()
      .sort(
        (a, b) => Number(a?.seedAmount || 0) - Number(b?.seedAmount || 0)
      )[0];
  }, [chainsListData]);

  const chainSeedLookup = useMemo(() => {
    const byId = new Map();
    const byName = new Map();
    (Array.isArray(chainsListData) ? chainsListData : []).forEach((chain) => {
      const seedAmount = Number(chain?.seedAmount ?? chain?.seedMoney ?? 0);
      if (!Number.isFinite(seedAmount)) return;

      const idKeys = [chain?._id, chain?.id, chain?.chainId]
        .map((value) => String(value || "").trim())
        .filter(Boolean);
      idKeys.forEach((key) => byId.set(key, seedAmount));

      const nameKeys = [chain?.name, chain?.chainName]
        .map((value) => String(value || "").trim().toLowerCase())
        .filter(Boolean);
      nameKeys.forEach((key) => byName.set(key, seedAmount));
    });

    return { byId, byName };
  }, [chainsListData]);



  const searchNodes = useMutation({
    mutationFn: searchNodeLazy,
  });

  // Load settings first — everything else gates on these flags.
  const { data: landingSettings } = useQuery({
    queryKey: ["landingSettings"],
    queryFn: getThresholdValueLazy,
    staleTime: 10 * 60 * 1000,
    gcTime: 30 * 60 * 1000,
    refetchOnWindowFocus: false,
    refetchOnMount: false,
  });

  // Derived flags — default true so sections show once settings load.
  const showTrendingEnabled =
    landingSettings !== undefined
      ? landingSettings?.data?.showTrendingCampaignsSection !== false
      : false;
  const showFeaturedEnabled =
    landingSettings !== undefined
      ? landingSettings?.data?.showFeaturedCampaignsSection !== false
      : false;

  // Only fetch top nodes when the trending section is turned on.
  const { data: top10Nodes } = useQuery({
    queryKey: ["TopNodes"],
    queryFn: getTopNodesLazy,
    enabled: showTrendingEnabled || showFeaturedEnabled,
  });

  // Only fetch featured campaigns when the featured section is turned on.
  const { data: featuredCampaigns = [] } = useQuery({
    queryKey: ["featuredCampaigns"],
    queryFn: () => getFeaturedCampaignsLazy(50),
    enabled: showFeaturedEnabled,
  });

  const heroCopy = useMemo(() => {
    const hero = landingSettings?.data?.landingHeroText || {};
    return {
      line1: (hero?.line1 || DEFAULT_HERO_COPY.line1).trim(),
      line2: (hero?.line2 || DEFAULT_HERO_COPY.line2).trim(),
      line3: (hero?.line3 || DEFAULT_HERO_COPY.line3).trim(),
    };
  }, [landingSettings]);

  // Re-use the flags already derived above (showTrendingEnabled / showFeaturedEnabled)
  // for the render-side conditionals so we have a single source of truth.
  const showTrendingCampaignsSection = showTrendingEnabled;
  const showFeaturedCampaignsSection = showFeaturedEnabled;
  const showTrendingFundColumn =
    landingSettings?.data?.showTrendingFundColumn !== false;
  const showTrendingUserColumn =
    landingSettings?.data?.showTrendingUserColumn !== false;
  const trendingSortBy =
    landingSettings?.data?.trendingSortBy === "fund" ? "fund" : "followers";
  const suppressZeroFundAndFollowersCampaigns =
    landingSettings?.data?.suppressZeroFundAndFollowersCampaigns === true;
  const trendingCampaignsLimit = Math.max(
    1,
    Number(landingSettings?.data?.trendingCampaignsLimit) || 10
  );

  const trendingNodes = useMemo(() => {
    let nodes = Array.isArray(state.tenNodes) ? [...state.tenNodes] : [];
    if (suppressZeroFundAndFollowersCampaigns) {
      nodes = nodes.filter((item) => {
        const fund = Number(item?.totalEarning || 0);
        const followers = Number(item?.totalMembers || 0);
        return !(fund === 0 && followers === 0);
      });
    }
    nodes.sort((a, b) => {
      if (trendingSortBy === "fund") {
        return Number(b?.totalEarning || 0) - Number(a?.totalEarning || 0);
      }
      return Number(b?.totalMembers || 0) - Number(a?.totalMembers || 0);
    });
    return nodes.slice(0, trendingCampaignsLimit);
  }, [
    state.tenNodes,
    trendingSortBy,
    trendingCampaignsLimit,
    suppressZeroFundAndFollowersCampaigns,
  ]);

  const featuredNodes = useMemo(() => {
    const configuredLimit = Math.max(
      1,
      Number(landingSettings?.data?.featuredCampaignsLimit) || 6
    );
    const fallbackNodes = Array.isArray(top10Nodes) ? top10Nodes : [];
    const sourceNodes =
      Array.isArray(featuredCampaigns) && featuredCampaigns.length
        ? featuredCampaigns
        : fallbackNodes;
    if (!sourceNodes.length) return [];

    const sortedByFeaturedTime = [...sourceNodes].sort(
      (a, b) => getFeaturedTime(b) - getFeaturedTime(a)
    );

    const splitIndex = Math.ceil(sortedByFeaturedTime.length / 2);
    const newerHalf = sortedByFeaturedTime.slice(0, splitIndex);
    const olderHalf = sortedByFeaturedTime.slice(splitIndex);

    const newerShuffled = shuffleBySeed(
      newerHalf,
      featuredMixSeedRef.current + 11
    );
    const olderShuffled = shuffleBySeed(
      olderHalf,
      featuredMixSeedRef.current + 29
    );

    const mixed = [];
    let newIndex = 0;
    let oldIndex = 0;
    while (mixed.length < sortedByFeaturedTime.length) {
      if (newIndex < newerShuffled.length) {
        mixed.push(newerShuffled[newIndex]);
        newIndex += 1;
      }
      if (oldIndex < olderShuffled.length) {
        mixed.push(olderShuffled[oldIndex]);
        oldIndex += 1;
      }
      if (newIndex >= newerShuffled.length && oldIndex >= olderShuffled.length) {
        break;
      }
    }

    return mixed.slice(0, configuredLimit);
  }, [featuredCampaigns, top10Nodes, landingSettings?.data?.featuredCampaignsLimit]);

  const featuredMarqueeSourceItems = useMemo(() => {
    return featuredNodes.length > 1 ? [...featuredNodes, ...featuredNodes] : [...featuredNodes];
  }, [featuredNodes]);

  const featuredMarqueeItems = useMemo(() => {
    return featuredMarqueeSourceItems;
  }, [featuredMarqueeSourceItems]);

  const featuredMarqueeDuration = useMemo(() => {
    const baseCount = Math.max(featuredNodes.length, 3);
    return `${Math.max(24, baseCount * 8)}s`;
  }, [featuredNodes.length]);

  const featuredMarqueeViewportRef = useRef(null);
  const featuredCardRefs = useRef([]);
  const featuredDragStateRef = useRef({
    isDragging: false,
    pointerId: null,
    startX: 0,
    startScrollLeft: 0,
    moved: false,
  });
  const featuredSuppressClickRef = useRef(false);
  const [isFeaturedDragging, setIsFeaturedDragging] = useState(false);
  const [visibleFeaturedCards, setVisibleFeaturedCards] = useState({});

  const getFeaturedLoopWidth = () => {
    const viewport = featuredMarqueeViewportRef.current;
    if (!viewport || featuredNodes.length <= 1) return 0;
    return viewport.scrollWidth / 2;
  };

  const normalizeFeaturedScroll = (rawValue) => {
    const loopWidth = getFeaturedLoopWidth();
    if (!loopWidth) return Math.max(0, rawValue);
    return ((rawValue % loopWidth) + loopWidth) % loopWidth;
  };

  useEffect(() => {
    if (!featuredMarqueeItems.length) {
      setVisibleFeaturedCards({});
      return;
    }

    setVisibleFeaturedCards((prev) => {
      const next = { ...prev };
      const initialVisibleCount = Math.min(4, featuredMarqueeItems.length);
      for (let index = 0; index < initialVisibleCount; index += 1) {
        next[index] = true;
      }
      return next;
    });
  }, [featuredMarqueeItems.length]);

  // Horizontal wheel support (Apple mouse/trackpad + regular mouse wheel).
  // We map wheel delta to scrollLeft while the pointer is over the marquee.
  useEffect(() => {
    const viewport = featuredMarqueeViewportRef.current;
    if (!viewport) return undefined;

    const onWheel = (event) => {
      const hasOverflow = viewport.scrollWidth > viewport.clientWidth + 1;
      if (!hasOverflow) return;

      const horizontalDelta = Math.abs(event.deltaX) > 0.01 ? event.deltaX : 0;
      const verticalDelta = Math.abs(event.deltaY) > 0.01 ? event.deltaY : 0;
      const delta = horizontalDelta || verticalDelta;
      if (!delta) return;

      viewport.scrollLeft = normalizeFeaturedScroll(viewport.scrollLeft + delta);
      event.preventDefault();
    };

    viewport.addEventListener("wheel", onWheel, { passive: false });
    return () => viewport.removeEventListener("wheel", onWheel);
  }, [featuredMarqueeItems.length]);

  useEffect(() => {
    const viewport = featuredMarqueeViewportRef.current;
    if (!viewport) return undefined;

    const onScroll = () => {
      const loopWidth = getFeaturedLoopWidth();
      if (!loopWidth) return;
      if (viewport.scrollLeft >= loopWidth || viewport.scrollLeft < 0) {
        viewport.scrollLeft = normalizeFeaturedScroll(viewport.scrollLeft);
      }
    };

    viewport.addEventListener("scroll", onScroll, { passive: true });
    return () => viewport.removeEventListener("scroll", onScroll);
  }, [featuredMarqueeItems.length]);

  const handleFeaturedPointerDown = (event) => {
    if (event.pointerType === "mouse" && event.button !== 0) return;
    if (event.target.closest("button, a, input, textarea, select")) return;

    const viewport = featuredMarqueeViewportRef.current;
    if (!viewport) return;

    const drag = featuredDragStateRef.current;
    drag.isDragging = true;
    drag.pointerId = event.pointerId;
    drag.startX = event.clientX;
    drag.startScrollLeft = viewport.scrollLeft;
    drag.moved = false;
    featuredSuppressClickRef.current = false;

    setIsFeaturedDragging(true);
    try {
      viewport.setPointerCapture(event.pointerId);
    } catch (_err) {
      // no-op
    }
  };

  const handleFeaturedPointerMove = (event) => {
    const viewport = featuredMarqueeViewportRef.current;
    const drag = featuredDragStateRef.current;
    if (!viewport || !drag.isDragging) return;

    const deltaX = event.clientX - drag.startX;
    if (Math.abs(deltaX) > 4) drag.moved = true;
    viewport.scrollLeft = normalizeFeaturedScroll(drag.startScrollLeft - deltaX);
  };

  const finishFeaturedDrag = () => {
    const drag = featuredDragStateRef.current;
    if (!drag.isDragging) return;

    featuredSuppressClickRef.current = drag.moved;
    drag.isDragging = false;
    drag.pointerId = null;
    drag.moved = false;
    setIsFeaturedDragging(false);

    if (featuredSuppressClickRef.current) {
      window.setTimeout(() => {
        featuredSuppressClickRef.current = false;
      }, 0);
    }
  };

  const handleFeaturedPointerUp = (event) => {
    const viewport = featuredMarqueeViewportRef.current;
    try {
      if (viewport?.hasPointerCapture(event.pointerId)) {
        viewport.releasePointerCapture(event.pointerId);
      }
    } catch (_err) {
      // no-op
    }
    finishFeaturedDrag();
  };

  const handleFeaturedPointerCancel = () => {
    finishFeaturedDrag();
  };

  const handleFeaturedClickCapture = (event) => {
    if (!featuredSuppressClickRef.current) return;
    event.preventDefault();
    event.stopPropagation();
  };

  useEffect(() => {
    const viewport = featuredMarqueeViewportRef.current;
    if (!viewport || !featuredMarqueeItems.length) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const index = Number(entry.target?.dataset?.featuredIndex);
          if (!Number.isFinite(index)) return;

          if (entry.isIntersecting) {
            setVisibleFeaturedCards((prev) =>
              prev[index] ? prev : { ...prev, [index]: true }
            );
          }
        });
      },
      {
        root: viewport,
        threshold: 0.15,
        rootMargin: "10% 10% 10% 10%",
      }
    );

    featuredCardRefs.current.forEach((node) => {
      if (node) observer.observe(node);
    });

    return () => observer.disconnect();
  }, [featuredMarqueeItems.length]);

  const featuredFallbackPresentation = useMemo(() => {
    if (!featuredNodes.length) return [];

    const seededOrder = shuffleWithSeed(
      FEATURED_FALLBACK_VARIANTS,
      hashString(
        `${featuredNodes
          .map((item) => String(item?._id || item?.campaignTitle || item?.chainName || ""))
          .join("|")}`
      )
    );

    return featuredNodes.map((item, index) => {
      const itemKey = String(
        item?._id || item?.campaignTitle || `featured-${index}`
      );
      const itemSeed = hashString(`${itemKey}-${index}`);
      return {
        variant: seededOrder[index % seededOrder.length],
        palette: createUniqueFeaturedPalette(index, itemSeed),
        phaseOffset: `-${2 + (itemSeed % 8)}s`,
      };
    });
  }, [featuredNodes]);



  useEffect(() => {
    setState((prev) => ({
      ...prev,
      tenNodes: top10Nodes,
      
    }));
  }, [top10Nodes]);

  // Deliberately skip automatic checkout warmup on generic landing-page load.
  // Unpaid signed-in users were hitting 403s here before they expressed intent
  // to start checkout. We still prefetch on explicit Join/Get Started clicks.

  // const {
  //   // data,
  //   refetch: getAgainUserProfile,
  //   // isPending,
  // } = useQuery({
  //   queryKey: ["user"],
  //   queryFn: () => getProfileData(),
  // });

  // const { refetch: getAgainUserProfile } = useQuery({
  //   queryKey: ["user"],
  //   queryFn: () => getProfileData(),
  //   enabled: !!token, // The query only runs if the token is truthy (not null)
  // });

  // P1.11 — input is now a navigation surface, not a live search. A pasted
  // 24-char ObjectId still jumps straight to the join flow; everything else
  // submits on Enter and lands on the dedicated /search results page.
  const OBJECT_ID_RE = /^[a-f0-9]{24}$/i;
  const handleSearchNode = (value) => {
    const v = (value || "").trim();
    if (OBJECT_ID_RE.test(v)) {
      searchNodes.mutate(v, {
        onSuccess: (data) => {
          navigate("/join-node", { state: data });
        },
      });
    }
  };
  const submitCampaignSearch = (raw) => {
    const v = String(raw || "").trim();
    if (OBJECT_ID_RE.test(v)) {
      navigate(`/${v}`);
      return;
    }
    if (v.length >= 2) {
      navigate(`/search?q=${encodeURIComponent(v)}`);
    }
  };

  useEffect(() => {
    if (paymentId || chainId || nodesId) {
       //console.log({ paymentId, chainId, nodesId, paymnetnodeId, paymnetchainID });
      // Post-sign-in landing with chain/payment context: redirect to
      // /onboarding instead of auto-opening the legacy modal.
      if (isOnboardingPageEnabled()) {
        const qs = new URLSearchParams();
        if (paymentId) qs.set("paymentId", paymentId);
        if (chainId) qs.set("chainId", chainId);
        if (nodesId) qs.set("nodesId", nodesId);
        const q = qs.toString();
        navigate(`${ONBOARDING_PATH}${q ? `?${q}` : ""}`, { replace: true });
        return;
      }
      setIsOpen(true);
    }
  }, [paymentId, chainId, nodesId, paymnetnodeId, paymnetchainID]);

  useEffect(() => {}, [searchNodes.data]);

  const userData = useSelector((state) => state.auth.userData);

  const balance = userData?.userWallet?.userBalance
    ? userData?.userWallet?.userBalance + userData?.userWallet?.userFakeBalance
    : 0;

  useEffect(() => {
    const root = landingRootRef.current;
    if (!root) return;

    const sections = root.querySelectorAll("[data-reveal]");
    if (!sections.length) return;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) {
      sections.forEach((section) => section.classList.add("reveal-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          if (entry.isIntersecting) {
            // One-shot reveal: opacity/translate settle in and stay.
            el.classList.add("reveal-visible");
            // Replayable edge glow: strip then re-add on next frame so the
            // keyframe restarts every time the section re-enters the viewport.
            el.classList.remove("reveal-edge");
            // Force reflow so the animation actually replays.
            // eslint-disable-next-line no-unused-expressions
            void el.offsetWidth;
            el.classList.add("reveal-edge");
          } else {
            // Only drop the edge-glow class. Keep reveal-visible so the
            // element doesn't shift back down and re-trigger the observer
            // (that was causing flicker at partial-visibility scroll stops).
            el.classList.remove("reveal-edge");
          }
        });
      },
      {
        threshold: 0.16,
        rootMargin: "0px 0px -12% 0px",
      }
    );

    sections.forEach((section) => observer.observe(section));

    return () => observer.disconnect();
  }, []);

  return (
    <GlassBackground ref={landingRootRef}>
      <style>{`
        .featured-marquee-viewport {
          overflow-x: auto;
          overflow-y: hidden;
          position: relative;
          cursor: grab;
          scrollbar-width: none;
          -ms-overflow-style: none;
        }

        .featured-marquee-viewport::-webkit-scrollbar {
          display: none;
        }

        .featured-marquee-viewport.is-dragging {
          cursor: grabbing;
          user-select: none;
        }

        .featured-marquee-track {
          display: flex;
          align-items: stretch;
          width: max-content;
          gap: 0;
          will-change: transform;
        }

        .featured-marquee-track.is-animated {
          animation: featured-marquee-slide var(--featured-marquee-duration, 32s) linear infinite;
        }

        .featured-marquee-viewport:hover .featured-marquee-track,
        .featured-marquee-viewport:focus-within .featured-marquee-track {
          animation-play-state: paused;
        }

        .featured-marquee-card {
          flex: 0 0 clamp(270px, 28vw, 360px);
          contain: layout paint style;
        }

        .featured-join-cta {
          transform: scale(1);
          transform-origin: center;
          transition: transform 180ms cubic-bezier(0.22, 0.61, 0.36, 1), box-shadow 180ms cubic-bezier(0.22, 0.61, 0.36, 1);
        }

        .featured-view-cta {
          transform: translateY(0);
          transition: transform 180ms cubic-bezier(0.22, 0.61, 0.36, 1), box-shadow 180ms cubic-bezier(0.22, 0.61, 0.36, 1);
        }

        .featured-marquee-card:hover .featured-view-cta,
        .featured-marquee-card:focus-within .featured-view-cta {
          transform: translateY(-2px);
          box-shadow: 0 6px 14px rgba(17, 24, 39, 0.16);
        }

        .featured-marquee-card:hover .featured-join-cta,
        .featured-marquee-card:focus-within .featured-join-cta {
          transform: scale(1.2);
          animation: featured-join-wiggle 460ms ease-in-out infinite;
          box-shadow: 0 8px 18px rgba(17, 24, 39, 0.24);
        }

        .featured-marquee-card:hover:has(.featured-view-cta:hover) .featured-join-cta,
        .featured-marquee-card:focus-within:has(.featured-view-cta:focus-visible) .featured-join-cta {
          transform: scale(1);
          animation: none;
          box-shadow: none;
        }

        .featured-marquee-connector {
          flex: 0 0 76px;
          display: inline-flex;
          align-items: center;
          justify-content: center;
          color: #97a4b8;
          user-select: none;
        }

        .featured-marquee-connector-svg {
          overflow: visible;
        }

        .featured-marquee-connector-path {
          fill: none;
          stroke: currentColor;
          stroke-width: 2.2;
          stroke-linecap: round;
          stroke-dasharray: 1.2 8.2;
          opacity: 0.85;
        }

        .connector-variant-1 {
          transform: rotate(-7deg) translateY(-1px);
        }

        .connector-variant-2 {
          transform: rotate(8deg) translateY(2px);
        }

        .connector-variant-3 {
          transform: rotate(-3deg) translateY(1px);
        }

        .connector-variant-4 {
          transform: rotate(5deg) translateY(-2px);
        }

        .connector-variant-5 {
          transform: rotate(-10deg) translateY(1px);
        }

        @keyframes featured-marquee-slide {
          from {
            transform: translate3d(0, 0, 0);
          }
          to {
            transform: translate3d(-50%, 0, 0);
          }
        }

        @keyframes featured-join-wiggle {
          0%,
          100% {
            transform: scale(1.2) rotate(0deg);
          }
          20% {
            transform: scale(1.2) rotate(-4deg);
          }
          40% {
            transform: scale(1.2) rotate(4deg);
          }
          60% {
            transform: scale(1.2) rotate(-3deg);
          }
          80% {
            transform: scale(1.2) rotate(3deg);
          }
        }

        .featured-media-fallback {
          position: relative;
          width: 100%;
          height: 100%;
          overflow: hidden;
          display: flex;
          align-items: flex-end;
          padding: 12px;
          isolation: isolate;
          background:
            radial-gradient(120% 120% at 100% 0%, var(--featured-fallback-c2) 0%, transparent 58%),
            linear-gradient(135deg, var(--featured-fallback-c1) 0%, var(--featured-fallback-c3) 100%);
        }

        .featured-media-fallback__flow {
          position: absolute;
          inset: -20% -15%;
          z-index: 1;
          pointer-events: none;
          background:
            conic-gradient(
              from 0deg,
              transparent 0deg,
              color-mix(in srgb, var(--featured-fallback-c2) 75%, #ffffff 25%) 58deg,
              transparent 130deg,
              color-mix(in srgb, var(--featured-fallback-c1) 70%, #ffffff 30%) 215deg,
              transparent 290deg,
              color-mix(in srgb, var(--featured-fallback-c3) 82%, #ffffff 18%) 360deg
            );
          opacity: 0.38;
          filter: blur(5px);
          transform-origin: center;
        }

        .featured-media-fallback::before,
        .featured-media-fallback::after {
          content: "";
          position: absolute;
          pointer-events: none;
          border-radius: 9999px;
          z-index: 1;
        }

        .featured-media-fallback::before {
          width: 180px;
          height: 180px;
          top: -70px;
          right: -40px;
          background: radial-gradient(circle, color-mix(in srgb, var(--featured-fallback-c2) 85%, #ffffff 15%) 0%, transparent 70%);
          opacity: 0.65;
        }

        .featured-media-fallback::after {
          width: 210px;
          height: 210px;
          bottom: -115px;
          left: -75px;
          background: radial-gradient(circle, color-mix(in srgb, var(--featured-fallback-c1) 82%, #ffffff 18%) 0%, transparent 72%);
          opacity: 0.55;
        }

        .featured-media-fallback__overlay {
          position: absolute;
          inset: 0;
          z-index: 2;
          background:
            linear-gradient(180deg, rgba(8, 10, 17, 0.02) 0%, rgba(8, 10, 17, 0.2) 88%),
            repeating-linear-gradient(
              115deg,
              rgba(255, 255, 255, 0.025) 0px,
              rgba(255, 255, 255, 0.025) 1px,
              transparent 1px,
              transparent 9px
            );
        }

        .featured-media-fallback__content {
          position: relative;
          z-index: 3;
          max-width: 100%;
          text-wrap: balance;
        }

        .featured-media-fallback--drift {
          animation: featured-fallback-drift 7.2s ease-in-out infinite alternate;
        }

        .featured-media-fallback--drift .featured-media-fallback__flow {
          animation: featured-flow-spin 8s linear infinite;
        }

        .featured-media-fallback--drift::before {
          animation: featured-orb-right 5.8s ease-in-out infinite alternate;
        }

        .featured-media-fallback--drift::after {
          animation: featured-orb-left 6.5s ease-in-out infinite alternate;
        }

        .featured-media-fallback--pulse {
          animation: featured-fallback-pulse 4.8s ease-in-out infinite;
        }

        .featured-media-fallback--pulse .featured-media-fallback__flow {
          animation: featured-flow-breathe 4.6s ease-in-out infinite;
        }

        .featured-media-fallback--pulse::before,
        .featured-media-fallback--pulse::after {
          animation: featured-orb-pulse 4.1s ease-in-out infinite;
        }

        .featured-media-fallback--cross {
          animation: featured-fallback-cross 5.6s linear infinite alternate;
        }

        .featured-media-fallback--cross .featured-media-fallback__flow {
          animation: featured-flow-sweep 5.2s ease-in-out infinite alternate;
        }

        .featured-media-fallback--cross::before {
          animation: featured-orb-cross-right 3.8s ease-in-out infinite alternate;
        }

        .featured-media-fallback--cross::after {
          animation: featured-orb-cross-left 4.3s ease-in-out infinite alternate;
        }

        @keyframes featured-fallback-drift {
          0% {
            background-position: 100% 0%, 0% 0%;
            filter: saturate(1);
          }
          100% {
            background-position: 76% 20%, 100% 100%;
            filter: saturate(1.16);
          }
        }

        @keyframes featured-fallback-pulse {
          0%,
          100% {
            transform: scale(1);
            filter: brightness(1);
          }
          50% {
            transform: scale(1.02);
            filter: brightness(1.06);
          }
        }

        @keyframes featured-flow-spin {
          0% {
            transform: rotate(0deg) scale(1);
            opacity: 0.36;
          }
          50% {
            opacity: 0.5;
          }
          100% {
            transform: rotate(360deg) scale(1.08);
            opacity: 0.36;
          }
        }

        @keyframes featured-flow-breathe {
          0%,
          100% {
            transform: scale(0.92) rotate(0deg);
            opacity: 0.34;
          }
          50% {
            transform: scale(1.08) rotate(18deg);
            opacity: 0.52;
          }
        }

        @keyframes featured-flow-sweep {
          0% {
            transform: translate3d(-8%, -10%, 0) rotate(-10deg);
            opacity: 0.34;
          }
          100% {
            transform: translate3d(10%, 10%, 0) rotate(16deg);
            opacity: 0.5;
          }
        }

        @keyframes featured-fallback-cross {
          0% {
            background-position: 92% 8%, 0% 100%;
          }
          100% {
            background-position: 70% 24%, 100% 0%;
          }
        }

        @keyframes featured-orb-right {
          0% {
            transform: translate3d(0, 0, 0) scale(1);
          }
          100% {
            transform: translate3d(-10px, 12px, 0) scale(1.08);
          }
        }

        @keyframes featured-orb-left {
          0% {
            transform: translate3d(0, 0, 0) scale(1);
          }
          100% {
            transform: translate3d(16px, -11px, 0) scale(1.06);
          }
        }

        @keyframes featured-orb-pulse {
          0%,
          100% {
            transform: scale(1);
            opacity: 0.56;
          }
          50% {
            transform: scale(1.14);
            opacity: 0.72;
          }
        }

        @keyframes featured-orb-cross-right {
          0% {
            transform: translate3d(-6px, 8px, 0);
          }
          100% {
            transform: translate3d(11px, -8px, 0);
          }
        }

        @keyframes featured-orb-cross-left {
          0% {
            transform: translate3d(8px, -6px, 0);
          }
          100% {
            transform: translate3d(-12px, 7px, 0);
          }
        }

        @media (prefers-reduced-motion: reduce) {
          .featured-marquee-track,
          .featured-marquee-track.is-animated {
            animation: none !important;
            transform: none !important;
          }

          .featured-media-fallback,
          .featured-media-fallback__flow,
          .featured-media-fallback::before,
          .featured-media-fallback::after {
            animation: none !important;
            transform: none !important;
            filter: none !important;
          }
        }
      `}</style>
      <div
        className="relative 4xll:paddingX44 4xl:paddingX40 3xll:paddingX36 3xl:paddingX36 2xll:paddingX32 2xl:paddingX28 xl:paddingX lg:paddingX20 md:paddingX16 sm:paddingX12 4xll:pt-[40px] 4xl:pt-[34px] 3xll:pt-[28px] 3xl:pt-[22px] 2xll:pt-[20px] 2xl:pt-[20px] px-8"
      >
        {/* row one  */}
        <div
          className="relative z-10 xl:pt-16 lg:pt-14 md:pt-12 sm:pt-10 pt-6 xl:pb-28 lg:pb-24 md:pb-20 sm:pb-16 pb-12 flex min-h-[calc(100svh-88px)] flex-col items-center justify-center gap-y-12 lg:gap-y-16"
        >
          {/* row one centered hero copy — sits directly on the gradient */}
          <div className="w-full max-w-6xl mx-auto text-center flex flex-col items-center">
            <p className="4xll:text-48 4xl:text-44 3xll:text-40  3xl:text-36 2xll:text-32 2xl:text-28 text-xl  text-secondary font-extralight mb-2 xl:mb-2 lg:mb-2 md:mb-2 xl:leading-[30px] lg:leading-[26px] md:leading-[22px]">
              {heroCopy.line1}
            </p>
            <h1 className="glass-heading-hero 4xll:pt-16 4xl:pt-16 3xll:pt-14 3xl:pt-14 2xll:pt-[3rem] 2xl:pt-12 4xll:mb-14 4xl:mb-12 3xll:mb-11 3xl:mb-10 2xll:mb-9 2xl:mb-9 xl:mb-7 lg:mb-7 md:mb-6 sm:mb-5 xs:mb-4">
              {heroCopy.line2}
            </h1>
            <p className="  4xll:text-40 4xl:text-38 3xll:text-36  3xl:text-34 2xll:text-32 2xl:text-30  xl:text-28 lg:text-26 md:text-22  xs:text-16 4xll:mb-14 4xl:mb-12 3xll:mb-11  3xl:mb-10  2xll:mb-9 2xl:mb-9 xl:mb-10 lg:mb-9 md:mb-8 sm:mb-7  tracking-[1px] leading-[30px] ">
              {heroCopy.line3}
            </p>
            <div className="xs:pt-3.5 4xll:pt-16  4xl:pt-16  3xll:pt-14   3xl:pt-14  2xll:pt-12  2xl:pt-12 flex flex-col items-center gap-4">
              <div className="hero-cta-glow flex flex-row flex-wrap items-center justify-center gap-4 mb-2">
                <Button
                  btnText="Join the Movement"
                  bgColor="#000000"
                  textColor="#FFFFFF"
                  paddingY="10px"
                  borderradius="9px"
                  breakpoint="  4xll:w-[700px] 4xl:w-[600px] 3xll:w-[500px]  3xl:w-[400px] 2xll:w-[350px] 2xl:w-[300px]   sm:w-[180px] xs:w-[180px] md:w-[180px] lg:w-[200px] xl:w-[221px]"
                  onClick={() => {
                    if (token) {
                      navigate(ONBOARDING_PATH);
                    } else {
                      navigate("/sign-in?intent=get-started");
                    }
                  }}
                />
              </div>

              <HeroAnimatedLine />

              <div className="flex flex-row items-center gap-4 mt-2">
                <a
                  href="/node-access"
                  className="flex items-center text-sm text-gray-600 underline underline-offset-2 hover:text-gray-900 transition-colors"
                >
                  Got an Invite?
                </a>
                <a
                  href="/howitworks"
                  className="text-sm text-gray-600 underline underline-offset-2 hover:text-gray-900 transition-colors"
                >
                  How It Works
                </a>
              </div>
            </div>
          </div>

          {/* Hero copy is now full-width centered. The video carousel was
              moved to /howitworks per product direction. */}
        </div>

        {/* Streams list removed from the home page — stream selection
            now lives entirely on /onboarding. We keep the chains-list
            query active above so the cache (and the Stripe / PayPal
            keys it carries) is warm by the time the user clicks Get
            Started. */}
      </div>
      {/* secure your financial journey sec  */}
      <div
        data-reveal
        className="reveal-on-scroll relative 4xll:py-[300px] 4xl:py-[250px] 3xll:py-[200px] 3xl:py-[150px] 2xll:py-[100px] 2xl:py-[100px] xl:py-28 lg:py-24 md:py-20 py-16 flex-col-center xl:paddingX lg:paddingX20 md:paddingX16 sm:paddingX12 px-8 xs:px-3"
      >
        <h1 className="glass-heading mb-6 text-ink text-center">
          Got an Invite?
        </h1>
        <p className="glass-heading-sm xl:mb-7 mb-4 text-ink text-center">
          Follow a campaign, to launch yours - for free.
        </p>
        <p className="4xll:pt-16 4xl:pt-16 3xll:pt-14 3xl:pt-14 2xll:pt-12 2xl:pt-12 4xll:text-50 4xl:text-46 3xll:text-44 3xl:text-40 2xll:text-38 2xl:text-34 xl:text-30 lg:text-26 md:text-22 text-18 text-ink-soft xl:mb-[119px] lg:mb-[90px] md:mb-[60px] mb-[40px]">
          We waive the hosting fee for your new campaign when you follow one.
        </p>
        <div className="4xll:pt-44  4xl:pt-40  3xll:pt-36   3xl:pt-32  2xll:pt-28  2xl:pt-24 flex-between xl:gap-4 lg:gap-3 md:gap-2  4xll:gap-x-[400px] 4xl:gap-x-[360px] 3xll:gap-x-[320px]  3xl:gap-x-[200px] 2xll:gap-x-[140px] 2xl:gap-x-[80px] ">
          {financeItems.map((item, idx) => (
            <div
              className="flex-col-between text-center xl:gap-3 gap-1 4xll:gap-14  4xl:gap-12 3xll:gap-10  3xl:gap-8 2xll:gap-6 2xl:gap-4 "
              key={idx}
            >
              <div className="glass-invite-icon-shell" aria-hidden="true">
                <InviteFeatureIcon
                  type={
                    idx === 0
                      ? "secure"
                      : idx === 1
                        ? "withdraw"
                        : "rewards"
                  }
                />
              </div>
              <h2 className="text-ink  4xll:text-50 4xl:text-46 3xll:text-44  3xl:text-40 2xll:text-38 2xl:text-34 xl:text-30 lg:text-26 md:text-22 sm:text-18 xs:text-[0.8rem] text-16 font-bold	">
                {item.title}
              </h2>
              <p className="text-ink-soft 4xll:text-48  4xl:text-44  3xll:text-40  3xl:text-36 2xll:text-32 2xl:text-28 xl:text-25 lg:text-22 md:text-18 xs:text-14 text-14 font-normal leading-[26px]">
                {item.desc}
              </p>
            </div>
          ))}
        </div>
        {/* Got an Invite button below all text and icons */}
        <div className="w-full flex justify-center mt-24">
          <Button
            variant="glass-primary"
            btnText="Got an Invite?"
            paddingY="14px"
            paddingX="32px"
            onClick={() => window.location.href = "/node-access"}
          />
        </div>
      </div>
      {/* node sec  */}
      <div
        data-reveal
        className="reveal-on-scroll 4xll:paddingX44 4xl:paddingX40 3xll:paddingX36 3xl:paddingX36 2xll:paddingX32 2xl:paddingX28 xl:paddingX lg:paddingX20 md:paddingX16 sm:paddingX12 px-8 4xll:pt-[260px] 4xl:pt-[220px] 3xll:pt-[180px] 3xl:pt-[140px] 2xll:pt-[110px] 2xl:pt-[100px] xl:pt-28 lg:pt-24 md:pt-20 sm:pt-16 pt-14 xl:pb-20 lg:pb-14 md:pb-10 sm:pb-8 pb-6"
        id="topcamps"
      >
        {showFeaturedCampaignsSection && featuredMarqueeItems.length > 0 && (
          <section className="mb-16 xl:mb-24">
            <div className="flex items-end justify-between gap-4 flex-wrap mb-8">
              <div>
                <h1 className="glass-heading mb-3">
                  Featured Campaigns
                </h1>
              </div>
            </div>

            <div
              ref={featuredMarqueeViewportRef}
              className={`mx-auto max-w-[1180px] featured-marquee-viewport ${isFeaturedDragging ? "is-dragging" : ""}`}
              onPointerDown={handleFeaturedPointerDown}
              onPointerMove={handleFeaturedPointerMove}
              onPointerUp={handleFeaturedPointerUp}
              onPointerCancel={handleFeaturedPointerCancel}
              onClickCapture={handleFeaturedClickCapture}
            >
              <div
                className={`featured-marquee-track ${featuredNodes.length > 1 ? "is-animated" : ""}`}
                style={{ "--featured-marquee-duration": featuredMarqueeDuration }}
              >
              {featuredMarqueeItems.map((item, itemIndex) => {
                const baseIndex = featuredNodes.length
                  ? itemIndex % featuredNodes.length
                  : itemIndex;

                const creatorName =
                  item?.userName || item?.username || item?.fullName || item?.name || "Creator";
                const campaignId = String(item?._id || "");
                const chainForJoin = String(item?.chain || item?.chainId || "");
                const chainNameKey = String(item?.chainName || "").trim().toLowerCase();
                const communitySeedAmount =
                  chainSeedLookup.byId.get(chainForJoin) ??
                  chainSeedLookup.byName.get(chainNameKey) ??
                  null;
                const communitySeedLabel =
                  communitySeedAmount != null && Number.isFinite(communitySeedAmount)
                    ? `$${formatNumber(communitySeedAmount)}`
                    : "—";
                const featuredJoinLabel =
                  communitySeedAmount != null && Number.isFinite(communitySeedAmount) && communitySeedAmount > 0
                    ? `Join for $${formatNumber(communitySeedAmount)}`
                    : "Join";
                const media = resolveCampaignMedia(item);
                const fallbackMeta = featuredFallbackPresentation[baseIndex] || {
                  variant:
                    FEATURED_FALLBACK_VARIANTS[
                      baseIndex % FEATURED_FALLBACK_VARIANTS.length
                    ],
                  palette:
                    DEFAULT_FALLBACK_PALETTES[
                      baseIndex % DEFAULT_FALLBACK_PALETTES.length
                    ],
                  phaseOffset: `-${itemIndex + 2}s`,
                };

                const openCampaign = () => {
                  openCampaignDetails(campaignId, chainForJoin);
                };

                const joinCampaign = () => {
                  if (!campaignId || !chainForJoin) return;
                  handleJoin(campaignId, chainForJoin, "chain", item?.campaignTitle || item?.chainName);
                };

                return (
                  <React.Fragment key={`${item._id || "featured"}-${itemIndex}`}>
                  <article
                    ref={(node) => {
                      featuredCardRefs.current[itemIndex] = node;
                    }}
                    data-featured-index={itemIndex}
                    role="button"
                    tabIndex={0}
                    onClick={openCampaign}
                    onKeyDown={(event) => {
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openCampaign();
                      }
                    }}
                    className="featured-marquee-card featured-card-glass overflow-hidden cursor-pointer"
                  >
                    <div className="relative w-full h-[130px] bg-white/30 sm:h-[145px] lg:h-[160px] xl:h-[160px] 2xl:h-[170px]">
                      {visibleFeaturedCards[itemIndex] && media.type === "image" ? (
                        <img
                          src={media.imageUrl}
                          alt={item?.campaignTitle || item?._id}
                          width="1200"
                          height="675"
                          loading="lazy"
                          decoding="async"
                          className="h-full w-full object-cover"
                        />
                      ) : visibleFeaturedCards[itemIndex] && media.type === "video" ? (
                        isDirectVideoUrl(media.videoUrl) ? (
                          <video
                            src={media.videoUrl}
                            className="h-full w-full object-cover pointer-events-none"
                            playsInline
                            muted
                            loop
                            autoPlay
                            preload="metadata"
                          />
                        ) : (
                          <div className="flex h-full w-full items-start bg-[linear-gradient(135deg,#111827,#1f2937)] px-3 py-3 text-left text-white sm:px-4 sm:py-4">
                            <div className="max-w-full">
                              <p className="text-[10px] uppercase tracking-[0.18em] text-white/60 sm:text-[11px]">Featured video</p>
                              <p className="mt-1.5 text-[14px] font-semibold leading-tight sm:mt-2 sm:text-[16px]">
                                {item?.campaignTitle || item?.chainName || item?._id}
                              </p>
                              <p className="mt-1 text-[11px] text-white/70 sm:text-[12px]">Video preview available</p>
                              <p className="mt-1 text-[11px] text-white/70 sm:text-[12px]">Tap card to watch campaign video</p>
                            </div>
                          </div>
                        )
                      ) : (
                        <div
                          className={`featured-media-fallback featured-media-fallback--${fallbackMeta.variant}`}
                          style={{
                            "--featured-fallback-c1": fallbackMeta.palette[0],
                            "--featured-fallback-c2": fallbackMeta.palette[1],
                            "--featured-fallback-c3": fallbackMeta.palette[2],
                          }}
                        >
                          <div
                            className="featured-media-fallback__flow"
                            style={{ animationDelay: fallbackMeta.phaseOffset }}
                          />
                          <div className="featured-media-fallback__overlay" />
                          <div className="featured-media-fallback__content">
                            <p className="text-[10px] uppercase tracking-[0.18em] text-white/70 sm:text-[11px]">Community featured</p>
                            <p className="mt-1.5 text-[14px] font-semibold leading-tight text-white sm:mt-2 sm:text-[16px]">
                              {item?.campaignTitle || item?.chainName || item?._id}
                            </p>
                            <p className="mt-1 text-[11px] text-white/80 sm:text-[12px]">Visual preview loading from community palette</p>
                          </div>
                        </div>
                      )}

                      {media.type === "video" && (
                        <div className="featured-video-play-overlay" aria-hidden="true">
                          <div className="flex h-11 w-11 items-center justify-center rounded-full border border-white/70 bg-black/28 backdrop-blur-sm shadow-[0_8px_20px_rgba(17,17,17,0.32)]">
                            <svg viewBox="0 0 24 24" className="h-5 w-5 text-white" fill="currentColor">
                              <path d="M8 6.8C8 5.9 9 5.35 9.76 5.84L17.26 10.94C17.95 11.39 17.95 12.61 17.26 13.06L9.76 18.16C9 18.65 8 18.1 8 17.2V6.8Z" />
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>

                    <div className="p-3.5 sm:p-4">
                      <div className="flex items-center justify-between gap-2 mb-2.5">
                        <span className="rounded-full border border-white/60 bg-white/55 backdrop-blur px-2.5 py-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-ink">
                          {item?.chainName || "Campaign"}
                        </span>
                        <span className="text-[11px] text-ink-muted truncate max-w-[48%] text-right">{communitySeedLabel}</span>
                      </div>

                      <h2 className="text-[15px] sm:text-[16px] font-semibold text-ink leading-tight line-clamp-2 min-h-[40px]">
                        {item?.campaignTitle || item?.chainName || item?._id}
                      </h2>
                      <p className="mt-1.5 text-[12px] leading-5 text-ink-soft line-clamp-2 min-h-[44px]">
                        {item?.campaignDescription || "This campaign is featured by the Eqlize Now team."}
                      </p>

                      <div className="mt-2.5 flex items-center gap-2">
                        <span className="text-[11px] font-semibold text-ink-muted">Creator:</span>
                        <img
                          src={item?.userImage || "https://placehold.co/64x64?text=EQN"}
                          alt={creatorName}
                          width="40"
                          height="40"
                          loading="lazy"
                          decoding="async"
                          className="h-7 w-7 rounded-full object-cover border border-white/70"
                        />
                        <p className="min-w-0 truncate text-[12px] font-semibold text-ink">
                          {creatorName}
                        </p>
                      </div>

                      <div className="mt-3 flex items-center gap-2 flex-wrap">
                        <button
                          type="button"
                          className="featured-view-cta btn-glass-ghost btn-glass-pill btn-glass-compact"
                          onClick={(event) => {
                            event.stopPropagation();
                            openCampaign();
                          }}
                        >
                          View
                        </button>

                        <button
                          type="button"
                          className="featured-join-cta btn-glass-primary btn-glass-pill btn-glass-compact disabled:opacity-50"
                          disabled={!campaignId || !chainForJoin}
                          onClick={(event) => {
                            event.stopPropagation();
                            joinCampaign();
                          }}
                        >
                          {featuredJoinLabel}
                        </button>
                      </div>
                    </div>
                  </article>
                  {itemIndex < featuredMarqueeItems.length - 1 && (
                    <div className="featured-marquee-connector" aria-hidden="true">
                      <svg
                        className={`featured-marquee-connector-svg connector-variant-${
                          (baseIndex % 5) + 1
                        }`}
                        viewBox="0 0 76 32"
                        width="76"
                        height="32"
                        focusable="false"
                        aria-hidden="true"
                      >
                        <path
                          d={FEATURED_CONNECTOR_CURVES[(hashString(String(item?._id || itemIndex)) + itemIndex) % FEATURED_CONNECTOR_CURVES.length]}
                          className="featured-marquee-connector-path"
                        />
                      </svg>
                    </div>
                  )}
                  </React.Fragment>
                );
              })}
              </div>
            </div>
          </section>
        )}

        {showTrendingCampaignsSection && (
          <>
            <h1 className="glass-heading mb-4 xs:mb-1">
              Fastest Growing Campaigns
            </h1>
            <p className="4xll:pt-36  4xl:pt-32  3xll:pt-28   3xl:pt-24  2xll:pt-20  2xl:pt-16 4xll:text-38 4xl:text-36 3xll:text-34  3xl:text-32 2xll:text-30 2xl:text-28 xl:text-25 lg:text-22 md:text-20 sm:text-18 text-16 text-gray8 xl:mb-[54px] lg:mb-[45px] md:mb-[30px] mb-[20px]">
              Growth hack: Launch your campaign by backing a top lister below. Their
              expansion fuels yours.
            </p>
            <div className="w-full  xl:mb-[122px] lg:mb-[90px] md:mb-[60px] mb-[35px] 4xll:pt-16  4xl:pt-16  3xll:pt-14   3xl:pt-14  2xll:pt-12  2xl:pt-12">
              {/* P1.11 — pressing Enter routes to /search; a pasted 24-char
                  ObjectId still short-circuits to the join flow. */}
              <div className="border-b border-black flex py-1 justify-center items-center font-normal w-full">
                <input
                  className="border-none focus:outline-none border-b px-3 w-full  4xll:text-40 4xl:text-38 3xll:text-34  3xl:text-30 2xll:text-25 2xl:text-22     "
                  placeholder={"Find Campaigns"}
                  type={"text"}
                  value={state.inputData}
                  onChange={(e) => {
                    setState((prev) => ({
                      ...prev,
                      inputData: e.target.value,
                    }));
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      submitCampaignSearch(state.inputData);
                    }
                  }}
                  onPaste={(e) => {
                    const pasted = e.clipboardData?.getData("text") ?? "";
                    handleSearchNode(pasted);
                  }}
                />
              </div>
            </div>
            {/* node sec table  */}

            <div className="4xll:py-[100px] overflow-x-auto xl:mb-10 lg:mb-8 md:mb-6 sm:mb-4 mb-4 ">
          <div className="inline-block min-w-full overflow-hidden">
            <table className="min-w-full leading-normal">
              <thead>
                <tr>
                  {showTrendingUserColumn && (
                    <th className="text-start px-2 py-3 border-b-[0.5px] border-gray9 4xll:text-50 4xl:text-46 3xll:text-44  3xl:text-40 2xll:text-36 2xl:text-32 xl:text-25 lg:text-22 md:text-18 sm:text-18 text-16 font-semibold">
                      User
                    </th>
                  )}
                  <th className="text-start px-2 py-3 border-b-[0.5px] border-gray9 4xll:text-50 4xl:text-46 3xll:text-44  3xl:text-40 2xll:text-36 2xl:text-32 xl:text-25 lg:text-22 md:text-18 sm:text-18 text-16">
                    Campaign ID
                  </th>
                  {showTrendingFundColumn && (
                    <th className="text-start px-2 py-3 border-b-[0.5px] border-gray9  4xll:text-50 4xl:text-46 3xll:text-44  3xl:text-40 2xll:text-36 2xl:text-32 xl:text-25 lg:text-22 md:text-18 sm:text-18 text-16 min-w-[150px]">
                      Fund
                    </th>
                  )}
                  <th className="text-start px-2 py-3 border-b-[0.5px] border-gray9  4xll:text-50 4xl:text-46 3xll:text-44  3xl:text-40 2xll:text-36 2xl:text-32 xl:text-25 lg:text-22 md:text-18 sm:text-18 text-16 min-w-[150px]">
                    Community
                  </th>
                  <th className="text-start px-2 py-3 border-b-[0.5px] border-gray9 4xll:text-50 4xl:text-46 3xll:text-44  3xl:text-40 2xll:text-36 2xl:text-32 xl:text-25 lg:text-22 md:text-18 sm:text-18 text-16 min-w-[150px]">
                    Supporters
                  </th>
                  <th className="text-start px-2 py-3 border-b-[0.5px] border-gray9 4xll:text-50 4xl:text-46 3xll:text-44  3xl:text-40 2xll:text-36 2xl:text-32 xl:text-25 lg:text-22 md:text-18 sm:text-18 text-16"></th>
                </tr>
              </thead>
              <tbody>
                {trendingNodes?.length > 0 &&
                  trendingNodes?.map((item, index) => {
                    const trendingChainId = String(item?.chain || item?.chainId || "");
                    const trendingChainNameKey = String(item?.chainName || "").trim().toLowerCase();
                    const trendingSeedAmount =
                      chainSeedLookup.byId.get(trendingChainId) ??
                      chainSeedLookup.byName.get(trendingChainNameKey) ??
                      Number(item?.seedAmount ?? item?.seedMoney ?? 0);
                    const trendingJoinLabel =
                      Number.isFinite(Number(trendingSeedAmount)) && Number(trendingSeedAmount) > 0
                        ? `Join for $${formatNumber(Number(trendingSeedAmount))}`
                        : "Join";
                    return (
                      <tr key={item._id}>
                        {showTrendingUserColumn && (
                          <td
                            className={`min-w-[150px] text-start xl:px-3 px-1 py-1 xl:py-3 4xll:text-44 4xl:text-40 3xll:text-38 3xl:text-34 2xll:text-30 2xl:text-22 xl:text-25 lg:text-22 md:text-20 sm:text-18 text-16`}
                          >
                            {item?.username || item?.userName || "-"}
                          </td>
                        )}
                        <td
                          onClick={() => {
                            openCampaignDetails(item?._id, item?.chain);
                          }}
                          className={`text-start  cursor-pointer xl:px-3 px-1 py-1 xl:py-3 4xll:text-44 4xl:text-40 3xll:text-38 3xl:text-34 2xll:text-30 2xl:text-22 xl:text-20 lg:text-18 md:text-16 sm:text-14 text-12 font-normal min-w-[200px]`}
                        >
                          {item._id}
                        </td>
                        {showTrendingFundColumn && (
                          <td
                            className={`text-start xl:px-3 px-1 py-1 xl:py-3 4xll:text-44 4xl:text-40 3xll:text-38 3xl:text-34 2xll:text-30 2xl:text-22 xl:text-25 lg:text-22 md:text-20 sm:text-18 text-16`}
                          >
                            ${formatNumber(item.totalEarning)}
                          </td>
                        )}
                        <th
                          className={`text-start  font-normal  px-2 py-3   4xll:text-50 4xl:text-46 3xll:text-44 3xl:text-40 2xll:text-36 2xl:text-32 xl:text-25 lg:text-22 md:text-18 sm:text-18 text-16 min-w-[150px]`}
                        >
                          {item?.chainName}
                        </th>
                        <td
                          className={`   text-start xl:px-3 px-1 py-1 xl:py-3 4xll:text-44 4xl:text-40 3xll:text-38 3xl:text-34 2xll:text-30 2xl:text-22 xl:text-25 lg:text-22 md:text-20 sm:text-18 text-16`}
                        >
                          {item.totalMembers}
                        </td>
                        <td className="text-start xl:px-3 px-1 py-1 xl:py-3 flex-center font-semibold">
                          <Button
                            variant="glass-primary"
                            pill
                            btnText={trendingJoinLabel}
                            paddingY="10px"
                            breakpoint=" 4xll:w-[400px] 4xl:w-[300px] 3xll:w-[250px] 3xl:w-[250px] 2xll:w-[200px] 2xl:w-[180px] sm:w-[99px] xs:w-[99px] md:w-[111px] lg:w-[122px] xl:w-[132px]"
                            onClick={() =>
                              handleJoin(
                                item._id,
                                item.chain,
                                "chain",
                                item.campaignTitle || item.chainName
                              )
                            }
                          />
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
            </div>
          </>
        )}
        {/* table end  */}

        <Modals
          isModalOpend={isopen}
          paymentmodal
          closeModal={handleToggle}
          // paymentId={paymentId || stripePaymentID}
          paymnetnodeId={paymnetnodeId?.nodeId}
          paymnetchainID={paymnetnodeId?.chainId}
          loginID={userData._id}
          joinodeChainId={chainId || chainn}
          nodesId={nodesId || aftersigninnodesId}
          // aftersigninnodesId={aftersigninnodesId}
        />
      </div>
      {/* /#topcamps end */}

      {/* get app sec row — pulled out of #topcamps but keeps the same
          padding scale so its content aligns horizontally with the Featured
          and Trending blocks above. */}
      <div
        data-reveal
        className="reveal-on-scroll 4xll:paddingX44 4xl:paddingX40 3xll:paddingX36 3xl:paddingX36 2xll:paddingX32 2xl:paddingX28 xl:paddingX lg:paddingX20 md:paddingX16 sm:paddingX12 px-8 4xll:pt-44 4xl:pt-40 3xll:pt-36 3xl:pt-32 2xll:pt-28 2xl:pt-24 xl:flex-start lg:flex-start 4xll:flex-start 4xl:flex-start 3xll:flex-start 3xl:flex-start 2xll:flex-start 2xl:flex-start md:flex-col"
      >
          <div className="xl:w-3/6 lg:w-3/6 w-full mb-[30px]">
            <h1 className="glass-heading xl:mb-[33px] lg:mb-[28px] mb-[20px] xs:text-center md:text-center sm:text-center">
              Download the apps!
            </h1>
            <p
              className=" 4xll:pt-16  4xl:pt-16  3xll:pt-14   3xl:pt-14  2xll:pt-12  2xl:pt-12 4xll:text-44 4xl:text-40 3xll:text-36  3xl:text-32 2xll:text-30 2xl:text-28 xl:text-25 lg:text-22 md:text-20 sm:text-18 text-16
 xl:mb-[39px] lg:mb-[32px] md:mb-[28px] mb-[20px]"
            >
              The mobile apps offer a convenient way to market your campaign,
              keep an eye on your wallet, chat with your supporters, scan QR codes,
              and more. Eligible users can also earn 100 points by downloading and
              logging into the mobile app (first mobile login). Don't miss out,
              download the apps today!
            </p>
            <div className="gap-[10px] flex-start   4xll:pt-36  4xl:pt-32  3xll:pt-28   3xl:pt-24  2xll:pt-20  2xl:pt-16 ">
              <div className="flex-col-start 4xll:w-[400px] w-[200px]">
                <h2 className="4xll:text-50 4xl:text-46 3xll:text-44  3xl:text-40 2xll:text-38 2xl:text-34 xl:text-18 lg:text-16 text-14  font-bold xl:mb-[18px] lg:mb-[15px] mb-[10px]">
                  iPhone:
                </h2>
                <a
                  href="https://apps.apple.com/app/eqlize-now/id6478133147"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={appstore}
                    alt="Download Eqlize Now on the App Store"
                    width="180"
                    height="53"
                    loading="lazy"
                    decoding="async"
                    className="4xll:w-[380px] 4xll:max-h-[125px] w-[180px] h-[auto] max-h-[53px] ml-[-10px] "
                  />
                </a>
              </div>
              <div className="flex-col-start 4xll:w-[400px] w-[200px]">
                <h2 className=" 4xll:text-50 4xl:text-46 3xll:text-44  3xl:text-40 2xll:text-38 2xl:text-34 xl:text-18 lg:text-16 text-14  font-bold xl:mb-[18px] lg:mb-[15px] mb-[10px]">
                  Android:
                </h2>
                <a
                  href="https://play.google.com/store/apps/details?id=com.ten_d"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <img
                    src={playstore}
                    alt="Get Eqlize Now on Google Play"
                    width="180"
                    height="53"
                    loading="lazy"
                    decoding="async"
                    className=" 
                               4xll:w-[380px] 4xll:max-h-[130px] w-[180px] h-[auto] max-h-[53px]     "
                  />
                </a>
              </div>
            </div>
          </div>
          <div className=" 4xll:w-[100%] 4xl:w-[100%] 3xll:w-[100%]  3xl:w-[100%] 2xll:w-[100%] 2xl:w-[100%]  xl:w-3/6 lg:w-3/6 w-full mb-[30px] flex-center">
            <img
              src={getAppRight}
              alt="getAppRightImg"
              width="900"
              height="780"
              loading="lazy"
              decoding="async"
              className="  4xll:w-[80%]  4xl:w-[70%] 3xll:w-[70%]  3xl:w-[65%] 2xll:w-[60%] 2xl:w-[55%] xl:max-w-full lg:max-w-[90%] max-w-[85%] text-center"
            />
          </div>
        </div>
      {/* node sec end  */}
      <div
        data-reveal
        className="reveal-on-scroll xl:paddingX lg:paddingX20 md:paddingX16 sm:paddingX12 px-8 xs:px-1 4xll:pt-[260px] 4xl:pt-[220px] 3xll:pt-[180px] 3xl:pt-[140px] 2xll:pt-[110px] 2xl:pt-[100px] xl:pt-28 lg:pt-24 md:pt-20 sm:pt-16 pt-14 pb-4"
      >
        <Suspense fallback={null}>
          <FAQ />
        </Suspense>
      </div>
      {/* bright future row  */}
      <div
        data-reveal
        className="reveal-on-scroll 4xll:pt-[260px] 4xl:pt-[220px] 3xll:pt-[180px] 3xl:pt-[140px] 2xll:pt-[110px] 2xl:pt-[100px] xl:pt-28 lg:pt-24 md:pt-20 sm:pt-16 pt-14"
      >
        <Suspense fallback={null}>
          <BrightFuture />
        </Suspense>
      </div>
    </GlassBackground>
  );
};

export default LandingPage;
